/**
 * SHARED with the Cloud Functions (backlog #115).
 *
 * Everything in `shared/` is imported by BOTH halves of the app: the web
 * bundle compiles it with `src/`, and `functions/scripts/build.mjs` inlines it
 * into `functions/lib/index.js` with esbuild, because Firebase uploads only
 * the `functions/` directory. There is no copy and no generated file — one
 * source of truth, edited here.
 *
 * Three rules follow from that:
 *
 * - **Stay pure.** No I/O, no Firebase SDK, no browser or node globals. It has
 *   to run in both places.
 * - **Import nothing but siblings in this directory** — no npm packages (the
 *   functions build leaves bare imports as runtime requires) and never back
 *   into `src/`. Both are lint-enforced and pinned by
 *   `functions/src/__tests__/sharedBoundary.test.ts`.
 * - **Type structurally.** No `src/types` imports; declare the minimum shape
 *   the function needs, so a caller on either side satisfies it.
 *
 * **A change here needs a `functions` deploy**, not just hosting — the CI
 * path filter watches `shared/**` for exactly that reason (Lessons #84, #94).
 */
/**
 * Deposit gate — pure helpers for the rule that gates MC creation.
 *
 * Measurement Confirmation creation is gated on the customer paying at least
 * the deposit percentage the sales rep set on the quote (50% legacy fallback).
 * The Cloud Function `createMeasurementFromQuote` enforces this on the server;
 * `offlineCreateFromQuote` mirrors the same rule client-side so the offline UI
 * behaves identically. The audit caught that the two paths were drifting —
 * extracting the pure rules here is the fix.
 *
 * CC surcharge (recorded as `surchargeAmount` on the ledger entry) is
 * excluded from the threshold so the surcharge can't accidentally satisfy the
 * deposit (CLAUDE.md "Recent Lessons #5").
 *
 * Cent-precision math (CLAUDE.md "Recent Lessons #16"): the comparison is in
 * integer cents with a 1-cent slack to absorb FP noise. The prior 0.001
 * ratio-tolerance was a S$25 free pass on a S$25k quote.
 */

import { formatSgd } from './money.ts';

/**
 * Quotes without an explicit `depositPercent` gate at 50%.
 *
 * KNOWN AND DELIBERATE SPLIT — 100% OFFERED, 50% GATED. Ben's call,
 * 2026-08-18; do NOT "fix" it to 100% without reading this.
 *
 * An absent `depositPercent` means two different things depending on who is
 * asking. The customer-facing side treats it as "pay in full":
 * `computePayableAmount(total, undefined)` returns the whole grand total, the
 * payment panel offers only that, and `orderSchedule` labels the milestone
 * `Deposit (100%)`. This gate treats it as 50%.
 *
 * Raising it to 100% would put the CONFIRMATION gate above the MINIMUM
 * `createPaymentIntent` charges: that minimum comes from
 * `resolveMinimumFirstPaymentBase` -> `evaluateDepositGate(undefined)` -> this
 * same 50%. A customer could then pay precisely the figure they were asked for
 * and still not be promoted — PARTIALLY_PAID, frozen, no order, which is
 * exactly the stuck state the payment-gated confirmation initiative exists to
 * remove. Closing the split therefore means moving the minimum too, in one
 * change, on the money path.
 *
 * It is also not cleanly closable today: "the rep toggled the deposit off" and
 * "this quote predates the field" both store `undefined`, so a 100% rule would
 * silently demand full payment from legacy quotes as well, and would desync
 * this gate from the MC gate that shares it.
 *
 * Unreachable through the current UI (the panel only ever offers the full total
 * on such a quote), but SLICE 3 redesigns what that panel offers — see
 * `docs/superpowers/specs/2026-08-05-payment-gated-quote-confirmation-design.md`.
 * If the split is to be closed, close it there, both halves together.
 *
 * ONE CUSTOMER-VISIBLE STRING DEPENDS ON THIS, and slice 3 should find it:
 * `createPaymentIntent`'s minimum-first-payment rejection renders
 * `This quotation requires a ${requiredPct}% deposit to confirm the order`
 * (`functions/src/airwallex/paymentIntent.ts`), and `requiredPct` comes from
 * `evaluateDepositGate` — so on a quote where the rep set NO deposit it reads
 * "requires a 50% deposit", which no rep ever chose. It is the only string in
 * the product that states gate behaviour rather than the amount asked for, and
 * it is unreachable today only because the panel cannot produce a below-minimum
 * payment on such a quote. Re-check it the moment the panel changes.
 * `formatDepositGateFailure` below renders `requiredPct` the same way, for the
 * measurement gate.
 */
export const LEGACY_MIN_DEPOSIT_RATIO = 0.5;

/** Minimum deposit a rep may set on a quote (the floor for an explicit
 *  `depositPercent`). Loosened from 50% to 10% — the deposit floats above this. */
export const MIN_DEPOSIT_RATIO = 0.1;

/** Acceptable rounding noise in the cent comparison. */
export const CENT_SLACK = 1;

export interface LedgerEntryLike {
  amount?: number;
  surchargeAmount?: number;
}

/**
 * Clamp the rep-supplied deposit percent into the legal range and convert to a
 * ratio. Unset → legacy 50%. Below the 10% floor is clamped up; above 100% is
 * clamped down. Single source of truth for both the MC gate and the money CFs'
 * deposit auto-advance (both import this file), so they can't desync.
 */
export const resolveDepositRatio = (depositPercent: number | null | undefined): number => {
  // 50%, not 100% — see LEGACY_MIN_DEPOSIT_RATIO above before changing this.
  if (depositPercent == null) return LEGACY_MIN_DEPOSIT_RATIO;
  return Math.min(1, Math.max(MIN_DEPOSIT_RATIO, depositPercent / 100));
};

/**
 * Sum the amount actually creditable toward the order total. CC surcharges
 * are excluded so they can't satisfy the deposit threshold.
 */
export const sumCreditablePayments = (ledger: ReadonlyArray<LedgerEntryLike> | undefined): number =>
  (ledger ?? []).reduce((sum, e) => sum + ((e.amount ?? 0) - (e.surchargeAmount ?? 0)), 0);

export interface DepositGateInput {
  ledger: ReadonlyArray<LedgerEntryLike> | undefined;
  grandTotal: number;
  depositPercent: number | null | undefined;
}

export interface DepositGateResult {
  /** True when the customer has paid enough to cross the threshold. */
  ok: boolean;
  /** Cent-precision: total paid in cents (excluding surcharge). */
  paidCents: number;
  /** Cent-precision: amount required, derived from `grandTotal * ratio`. */
  requiredCents: number;
  /** Required percentage of grand total, already resolved through
   *  `resolveDepositRatio` — so 10–100 for an explicit `depositPercent`
   *  (clamped at `MIN_DEPOSIT_RATIO`), and 50 when none is set. This is the
   *  figure `formatDepositGateFailure` renders into the customer-visible
   *  message below, so it must never be described as a range the picker no
   *  longer offers. */
  requiredPct: number;
  /** Sum of `amount - surchargeAmount` across the ledger. SGD. */
  totalPaid: number;
}

/**
 * Pure evaluation of the deposit gate. Does not throw; callers decide what to
 * do on failure (offline path throws a user-readable message; Cloud Function
 * throws `HttpsError('failed-precondition', ...)`).
 */
export const evaluateDepositGate = (input: DepositGateInput): DepositGateResult => {
  const totalPaid = sumCreditablePayments(input.ledger);
  const requiredRatio = resolveDepositRatio(input.depositPercent);
  const paidCents = Math.round(totalPaid * 100);
  const requiredCents = Math.round(input.grandTotal * requiredRatio * 100);
  const ok = paidCents >= requiredCents - CENT_SLACK;
  return {
    ok,
    paidCents,
    requiredCents,
    requiredPct: Math.round(requiredRatio * 100),
    totalPaid,
  };
};

/**
 * Format the user-facing failure message for a failed deposit gate. The text
 * matches the Cloud Function output so offline-dev and prod surface identical
 * errors. Callers wrap this in their own throw type (HttpsError vs Error).
 */
export const formatDepositGateFailure = (input: DepositGateInput): string => {
  const { totalPaid, requiredPct } = evaluateDepositGate(input);
  const grandTotal = input.grandTotal;
  const requiredRatio = resolveDepositRatio(input.depositPercent);
  const depositRatio = grandTotal > 0 ? totalPaid / grandTotal : 0;
  const minRequired = grandTotal * requiredRatio;
  const pct = Math.round(depositRatio * 100);
  return totalPaid <= 0
    ? `Measurement can only be scheduled once a ${requiredPct}% deposit is received. No payment recorded yet (need at least ${formatSgd(minRequired)}).`
    : `Measurement can only be scheduled once a ${requiredPct}% deposit is received. Currently received ${pct}% — ${formatSgd(totalPaid)} of ${formatSgd(grandTotal)} (need at least ${formatSgd(minRequired)}).`;
};
